import { useState, useRef, useEffect } from "react";
import {
  FormControl,
  MenuItem,
  Menu,
  Button,
  Typography,
  FormHelperText,
  Avatar,
  Box,
} from "@mui/material";
import muiCustomStyles from "../styles";

const SingleSelectDropdown = ({
  value,
  onChange,
  onBlur,
  showOptionImg = false,
  options,
  placeholder = "Select",
  width = "100%",
  buttonHeight = 39,
  disabled = false,
  error = false,
  helperText = "",
  horizontalAnchorOrigin = "center",
  sx = {},
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [buttonWidth, setButtonWidth] = useState(null);
  const buttonRef = useRef(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    const currentButton = buttonRef.current;

    const updateButtonWidth = () => {
      if (currentButton) {
        setButtonWidth(currentButton.offsetWidth);
      }
    };

    updateButtonWidth();

    const resizeObserver = new ResizeObserver(updateButtonWidth);
    if (currentButton) {
      resizeObserver.observe(currentButton);
    }

    return () => {
      if (currentButton) {
        resizeObserver.unobserve(currentButton);
      }
    };
  }, []);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    if (onBlur) {
      onBlur({ target: { value } });
    }
  };

  const handleMenuItemClick = async (newValue) => {
    await onChange(newValue);
    setAnchorEl(null);
    if (onBlur) {
      onBlur({ target: { value: newValue } });
    }
  };

  const selectedOption = options.find((item) => item.value === value);
  const {
    formControl,
    buttonBase,
    buttonOpen,
    text,
    menuPaper,
    menuItem,
    menuItemText,
  } = muiCustomStyles;

  return (
    <FormControl
      error={error}
      sx={{ ...formControl, minWidth: width, ...sx.formControl }}
    >
      <Button
        ref={buttonRef}
        disabled={disabled}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        variant="outlined"
        size="small"
        sx={{
          ...buttonBase,
          ...(open && buttonOpen),
          height: buttonHeight,
          borderColor: error ? "error.main" : "rgba(47, 43, 61, 0.23)",
          ...sx.button,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyItems: "center",
            alignItems: "center",
            gap: { xs: "8px", md: "12px" },
          }}
        >
          {showOptionImg ? (
            <Avatar
              alt={selectedOption?.label?.toUpperCase()}
              src={selectedOption?.imgUrl}
              sx={{ width: 38, height: 38 }}
            />
          ) : null}
          <Typography
            sx={{
              ...text,
              color: !value ? "text.disabled" : "text.primary",
              ...sx.text,
            }}
          >
            {selectedOption?.label || placeholder}
          </Typography>
        </Box>
        <img src="/icons/arrow-down.svg" alt="arrow-down" />
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{ "aria-labelledby": "dropdown-button" }}
        PaperProps={{
          style: {
            ...menuPaper,
            width: buttonWidth || "auto",
            ...sx.menuPaper,
          },
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: horizontalAnchorOrigin,
        }}
        transformOrigin={{
          horizontal: horizontalAnchorOrigin,
        }}
        sx={sx.menu}
      >
        {options.map((option, idx) => {
          const { label, imgUrl } = option;
          return (
            <MenuItem
              key={option.value}
              onClick={() => handleMenuItemClick(option.value)}
              selected={value === option.value}
              sx={{
                ...menuItem,
                mb: idx !== options.length - 1 ? "12px" : 0,
                ...sx.menuItem,
              }}
            >
              {showOptionImg ? (
                <Avatar
                  alt={label?.toUpperCase()}
                  src={imgUrl}
                  sx={{ width: 38, height: 38 }}
                />
              ) : null}
              <Typography sx={{ ...menuItemText, ...sx.menuItemText }}>
                {option.label}
              </Typography>
            </MenuItem>
          );
        })}
      </Menu>

      {helperText && (
        <FormHelperText sx={{ mt: "4px", ml: "0px", fontSize: "13px" }}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default SingleSelectDropdown;
